/**
 * Vendor Assessment API Service
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ============== Questionnaire Templates ==============

export const getQuestionnaireCategories = async () => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/questionnaire-categories`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getQuestionnaires = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/questionnaires`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getQuestionnaire = async (questionnaireId) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/questionnaires/${questionnaireId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Custom Questionnaires ==============

export const createCustomQuestionnaire = async (data) => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/questionnaires/custom`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateCustomQuestionnaire = async (questionnaireId, data) => {
  const response = await axios.put(`${API_URL}/api/vendor-assessment/questionnaires/custom/${questionnaireId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteCustomQuestionnaire = async (questionnaireId) => {
  const response = await axios.delete(`${API_URL}/api/vendor-assessment/questionnaires/custom/${questionnaireId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Vendors ==============

export const getVendors = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/vendors`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createVendor = async (data) => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/vendors`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getVendor = async (vendorId) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/vendors/${vendorId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateVendor = async (vendorId, data) => {
  const response = await axios.put(`${API_URL}/api/vendor-assessment/vendors/${vendorId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const deleteVendor = async (vendorId) => {
  const response = await axios.delete(`${API_URL}/api/vendor-assessment/vendors/${vendorId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Assessments ==============

export const getAssessments = async (params = {}) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/assessments`, {
    params,
    headers: getAuthHeaders()
  });
  return response.data;
};

export const createAssessment = async (data) => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/assessments`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const getAssessment = async (assessmentId) => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const updateAssessment = async (assessmentId, data) => {
  const response = await axios.put(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}`, data, {
    headers: getAuthHeaders()
  });
  return response.data;
};

export const submitAssessmentResponses = async (assessmentId, sectionId, responses) => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}/responses`, 
    { section_id: sectionId, responses },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const completeAssessment = async (assessmentId) => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}/complete`, 
    {},
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const reviewAssessment = async (assessmentId, action, notes = '') => {
  const response = await axios.post(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}/review`, 
    { action, notes },
    { headers: getAuthHeaders() }
  );
  return response.data;
};

export const deleteAssessment = async (assessmentId) => {
  const response = await axios.delete(`${API_URL}/api/vendor-assessment/assessments/${assessmentId}`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Dashboard ==============

export const getVendorDashboard = async () => {
  const response = await axios.get(`${API_URL}/api/vendor-assessment/dashboard`, {
    headers: getAuthHeaders()
  });
  return response.data;
};

// ============== Helper Functions ==============

export const getRiskLevelColor = (level) => {
  const colors = {
    high: 'red',
    medium: 'yellow',
    low: 'green',
    not_assessed: 'gray'
  };
  return colors[level] || 'gray';
};

export const getRiskLevelLabel = (level) => {
  const labels = {
    high: 'High Risk',
    medium: 'Medium Risk',
    low: 'Low Risk',
    not_assessed: 'Not Assessed'
  };
  return labels[level] || level;
};

export const getRiskLevelIcon = (level) => {
  const icons = {
    high: '🔴',
    medium: '🟡',
    low: '🟢',
    not_assessed: '⚪'
  };
  return icons[level] || '⚪';
};

export const getStatusColor = (status) => {
  const colors = {
    not_started: 'gray',
    in_progress: 'blue',
    pending_review: 'yellow',
    completed: 'green',
    expired: 'red'
  };
  return colors[status] || 'gray';
};

export const getStatusLabel = (status) => {
  const labels = {
    not_started: 'Not Started',
    in_progress: 'In Progress',
    pending_review: 'Pending Review',
    completed: 'Completed',
    expired: 'Expired'
  };
  return labels[status] || status;
};

export const getCategoryIcon = (categoryId) => {
  const icons = {
    security: '🛡️',
    privacy: '🔒',
    risk: '⚠️',
    compliance: '✅',
    vendor: '🏢',
    cloud: '☁️',
    saas: '🌐'
  };
  return icons[categoryId] || '📋';
};

export const getTierColor = (tier) => {
  const colors = {
    high: 'red',
    medium: 'yellow',
    low: 'green'
  };
  return colors[tier] || 'gray';
};

export const getTierLabel = (tier) => {
  const labels = {
    high: 'High Tier (Critical)',
    medium: 'Medium Tier',
    low: 'Low Tier'
  };
  return labels[tier] || tier;
};

export const getQuestionTypeIcon = (type) => {
  const icons = {
    single_choice: '○',
    multiple_choice: '☑',
    text: '📝',
    number: '#',
    date: '📅',
    file_upload: '📎',
    scale: '📊',
    yes_no: '✓/✗',
    yes_no_na: '✓/✗/N/A'
  };
  return icons[type] || '❓';
};

export const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

export default {
  getQuestionnaireCategories,
  getQuestionnaires,
  getQuestionnaire,
  createCustomQuestionnaire,
  updateCustomQuestionnaire,
  deleteCustomQuestionnaire,
  getVendors,
  createVendor,
  getVendor,
  updateVendor,
  deleteVendor,
  getAssessments,
  createAssessment,
  getAssessment,
  updateAssessment,
  submitAssessmentResponses,
  completeAssessment,
  reviewAssessment,
  deleteAssessment,
  getVendorDashboard,
  getRiskLevelColor,
  getRiskLevelLabel,
  getRiskLevelIcon,
  getStatusColor,
  getStatusLabel,
  getCategoryIcon,
  getTierColor,
  getTierLabel,
  getQuestionTypeIcon,
  formatDate,
  isOverdue
};
